import type { Language } from "@/contexts/LanguageContext";

export const SUPPORTED_LANGUAGES: Language[] = ["en", "pl", "de"];

/**
 * Returns the language encoded in the URL pathname, or null if none.
 * `/pl` or `/pl/...` → "pl", `/de` or `/de/...` → "de", otherwise null (EN default).
 */
export const getLanguageFromPath = (pathname: string): Language | null => {
  const match = pathname.match(/^\/(pl|de)(?:\/|$)/);
  if (!match) return null;
  return match[1] as Language;
};

/**
 * Strips the language prefix from a pathname. `/pl/contact` → `/contact`,
 * `/pl` → `/`, `/contact` → `/contact`.
 */
export const stripLangPrefix = (pathname: string): string => {
  const lang = getLanguageFromPath(pathname);
  if (!lang) return pathname;
  const stripped = pathname.slice(`/${lang}`.length);
  return stripped === "" ? "/" : stripped;
};

/**
 * Builds a pathname localized for the given target language. Keeps query
 * string and hash, only rewrites the path portion.
 *
 * Examples:
 *   buildLocalizedPath("/contact", "pl") → "/pl/contact"
 *   buildLocalizedPath("/pl/contact", "en") → "/contact"
 *   buildLocalizedPath("/pl/contact", "de") → "/de/contact"
 *   buildLocalizedPath("/", "pl") → "/pl"
 *   buildLocalizedPath("/pl", "en") → "/"
 */
export const buildLocalizedPath = (
  pathname: string,
  targetLang: Language
): string => {
  const base = stripLangPrefix(pathname);
  if (targetLang === "en") return base;
  // base starts with "/" - for "/" we want "/pl" not "/pl/"
  return base === "/" ? `/${targetLang}` : `/${targetLang}${base}`;
};
